import { Injectable } from '@angular/core';

export type BrowserNotificationPermission =
    | 'default'
    | 'granted'
    | 'denied'
    | 'unsupported';

/**
 * Avisos do sistema operacional, fora da aba (como o WhatsApp Web faz).
 *
 * Só aparece quando a aba **não** está em foco. Com a plataforma aberta na
 * frente do usuário, o sino e a faixa já avisam - somar um terceiro aviso
 * seria barulho.
 */
@Injectable({ providedIn: 'root' })
export class BrowserNotificationService {
    get permission(): BrowserNotificationPermission {
        if (typeof Notification === 'undefined') {
            return 'unsupported';
        }

        return Notification.permission as BrowserNotificationPermission;
    }

    //Só faz sentido oferecer o botão de ativar quando ainda dá para
    //perguntar: negado não se reverte por código, o usuário tem que mudar
    //na configuração do navegador.
    get canAsk(): boolean {
        return this.permission === 'default';
    }

    /**
     * Pede permissão. Precisa ser chamado a partir de um clique do usuário:
     * navegador moderno ignora (e penaliza) pedido automático ao abrir a
     * página, por isso não fazemos isso no login.
     */
    async requestPermission(): Promise<BrowserNotificationPermission> {
        if (typeof Notification === 'undefined') {
            return 'unsupported';
        }

        try {
            const result = await Notification.requestPermission();
            return result as BrowserNotificationPermission;
        } catch {
            return this.permission;
        }
    }

    /**
     * Mostra o aviso do sistema. Não faz nada se a permissão não foi dada
     * ou se a aba está visível - nesse caso o usuário já está vendo a
     * plataforma.
     */
    show(title: string, body?: string, onClick?: () => void): void {
        if (this.permission !== 'granted') {
            return;
        }

        if (typeof document !== 'undefined' && !document.hidden) {
            return;
        }

        try {
            const notification = new Notification(title, {
                body,
                icon: 'favicon-32x32.png',
                //Agrupa por tag: vários avisos seguidos substituem o
                //anterior em vez de empilhar uma pilha de balões.
                tag: 'geoqi-notification',
                renotify: false,
            } as NotificationOptions);

            notification.onclick = () => {
                //Traz a aba da plataforma para a frente antes de navegar.
                window.focus();
                notification.close();
                onClick?.();
            };
        } catch {
            //Navegador sem suporte ou bloqueio de contexto - segue sem o
            //aviso de sistema, que é um extra.
        }
    }
}
